import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Plus, Edit, Trash2, Building2, Settings, Truck, Package } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompaniesManagement = ({ getAuthHeaders }) => {
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [settingsCompany, setSettingsCompany] = useState(null);
  const [companySettings, setCompanySettings] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    subscription_tier: 'basic',
    is_active: true,
    slug: '',
    custom_domain: ''
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/admin/companies`, getAuthHeaders());
      setCompanies(response.data);
    } catch (error) {
      toast.error('Failed to fetch companies');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await axios.put(`${API}/admin/companies/${editingCompany.id}`, formData, getAuthHeaders());
        toast.success('Company updated successfully');
      } else {
        await axios.post(`${API}/admin/companies`, formData, getAuthHeaders());
        toast.success('Company created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await axios.delete(`${API}/admin/companies/${id}`, getAuthHeaders());
        toast.success('Company deleted successfully');
        fetchCompanies();
      } catch (error) {
        toast.error('Failed to delete company');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      subscription_tier: 'basic',
      is_active: true,
      slug: '',
      custom_domain: ''
    });
    setEditingCompany(null);
  };

  const openEditDialog = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      email: company.email,
      phone: company.phone || '',
      address: company.address || '',
      subscription_tier: company.subscription_tier,
      is_active: company.is_active,
      slug: company.slug || '',
      custom_domain: company.custom_domain || ''
    });
    setIsDialogOpen(true);
  };

  const openSettingsDialog = async (company) => {
    setSettingsCompany(company);
    try {
      const response = await axios.get(`${API}/admin/companies/${company.id}/settings`, getAuthHeaders());
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Failed to fetch company settings:', error);
      setCompanySettings({
        feature_flags: {},
        theme: {},
        preferences: {}
      });
    }
    setIsSettingsDialogOpen(true);
  };

  const handleSettingsUpdate = async () => {
    try {
      await axios.put(`${API}/admin/companies/${settingsCompany.id}/settings`, {
        feature_flags: companySettings.feature_flags
      }, getAuthHeaders());
      
      toast.success('Company settings updated successfully');
      setIsSettingsDialogOpen(false);
    } catch (error) {
      console.error('Failed to update company settings:', error);
      toast.error('Failed to update company settings');
    }
  };

  const toggleFeatureFlag = (flag) => {
    setCompanySettings(prev => ({
      ...prev,
      feature_flags: {
        ...prev.feature_flags,
        [flag]: !prev.feature_flags?.[flag]
      }
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>Companies</h2>
          <p className="text-gray-500">Manage tenant companies</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-gradient-to-r from-blue-600 to-purple-600" data-testid="create-company-button">
              <Plus className="w-4 h-4" />
              Add Company
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingCompany ? 'Edit Company' : 'Create Company'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Company Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="company-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="company-email-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="company-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  data-testid="company-address-input"
                />
              </div>
              <div>
                <Label htmlFor="slug">Slug (URL identifier)</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="Auto-generated from company name"
                  data-testid="company-slug-input"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for auto-generation</p>
              </div>
              <div>
                <Label htmlFor="custom_domain">Custom Domain (Premium)</Label>
                <Input
                  id="custom_domain"
                  value={formData.custom_domain}
                  onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value })}
                  placeholder="portal.company.com"
                  data-testid="company-domain-input"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1" data-testid="company-submit-button">
                  {editingCompany ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="text-center py-12">Loading...</div>
      ) : companies.length === 0 ? (
        <Card className="p-12 text-center">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No companies yet. Create your first company!</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {companies.map((company) => (
            <Card key={company.id} className="p-6 hover:shadow-lg transition-shadow" data-testid={`company-card-${company.id}`}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg" data-testid={`company-name-${company.id}`}>{company.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      company.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {company.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                <p data-testid={`company-email-${company.id}`}>{company.email}</p>
                {company.phone && <p>{company.phone}</p>}
                {company.slug && (
                  <p className="font-mono text-xs bg-gray-100 px-2 py-1 rounded" data-testid={`company-slug-${company.id}`}>
                    /t/{company.slug}
                  </p>
                )}
                {company.address && <p className="text-xs">{company.address}</p>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEditDialog(company)} className="flex-1" data-testid={`edit-company-${company.id}`}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => openSettingsDialog(company)} className="flex-1" data-testid={`settings-company-${company.id}`}>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDelete(company.id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-company-${company.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Settings Dialog */}
      <Dialog open={isSettingsDialogOpen} onOpenChange={setIsSettingsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Company Settings - {settingsCompany?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold mb-4">Feature Modules</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Truck className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">Driver Module</h4>
                      <p className="text-sm text-gray-500">Enable driver management, job assignment, and mobile dashboard</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={companySettings.feature_flags?.driver_module_enabled || false}
                      onChange={() => toggleFeatureFlag('driver_module_enabled')}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                {/* Add more feature flags here as needed */}
                <div className="flex items-center justify-between p-3 border rounded-lg opacity-50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Package className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-500">Advanced Inventory</h4>
                      <p className="text-sm text-gray-400">Coming soon - Inventory tracking and management</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      disabled
                      className="w-5 h-5 text-gray-400 rounded"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={handleSettingsUpdate} className="flex-1">
                Save Settings
              </Button>
              <Button variant="outline" onClick={() => setIsSettingsDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default CompaniesManagement;
